import { API_BASE_URL } from '../utils/constants';
import type { Session, PollingConfig, AuthLookupResult, Organization, PlatformConfig } from '../types/tenant';
import type {
  FleetSummary,
  UptimeTrend,
  PortfolioMetrics,
  Alert,
  Period,
  ServiceSummary,
  ResourceSummary,
  UserAudit,
  NodeLifecycleCounts,
  DeploymentStats,
  ClusterSummary,
  NetworkSummary,
} from '../types/metrics';
import type {
  TriageQueueResponse,
  TriagePriority,
  FleetRiskForecast,
  SecuritySummary,
  FirewallGroupItem,
  FirewallHitsByNode,
  RulesPerNetwork,
  DeniedTrafficPoint,
  DeniedPacketsPeriod,
} from '../types/metrics';
import type { NetworksResponse } from '../types/network';
import type { NodeSummary, NodeDetail, StateEvent, NodeHistory, NodeContext } from '../types/node';
import type { NodesResponse } from '../types/api';
import type { UserPageSummary, UsersResponse, UserDetail, RolePermissionsItem } from '../types/user';

class ApiError extends Error {
  code: string;
  details?: unknown;

  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

// Allow setting a tenant ID header for dev users switching orgs
let _devTenantId: string | null = null;
export function setDevTenantId(tenantId: string | null): void {
  _devTenantId = tenantId;
}

/**
 * Reads the per-user preview stage from localStorage on each request.
 * 'full' is the default (no preview); other values restrict visibility
 * server-side as if the caller were in that tier.
 */
function readPreviewStageHeader(): string | null {
  if (typeof window === 'undefined') return null;
  const stage = window.localStorage.getItem('insights.previewStage');
  if (stage === 'mvp' || stage === 'base' || stage === 'deep') return stage;
  // Legacy fallback — pre-stage-chips key.
  if (window.localStorage.getItem('insights.previewAsConsumer') === '1') return 'mvp';
  return null;
}

/** Return headers that include the dev tenant override (if set). Use for raw fetch() calls. */
export function getDevHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (_devTenantId) headers['X-Tenant-Id'] = _devTenantId;
  const previewStage = readPreviewStageHeader();
  if (previewStage) {
    headers['X-Preview-Stage'] = previewStage;
    // Keep legacy header set so older backends (pre-stage-chips) still respect MVP preview.
    if (previewStage === 'mvp') headers['X-Preview-As-Consumer'] = '1';
  }
  return headers;
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}, timeoutMs = 15_000): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }
  if (_devTenantId) {
    headers['X-Tenant-Id'] = _devTenantId;
  }
  const previewStage = readPreviewStageHeader();
  if (previewStage) {
    headers['X-Preview-Stage'] = previewStage;
    if (previewStage === 'mvp') headers['X-Preview-As-Consumer'] = '1';
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError('TIMEOUT', 'Request timed out. Please try again.');
    }
    throw err;
  }
  clearTimeout(timeoutId);

  const data = await response.json();

  if (!response.ok) {
    const error = data.error || { code: 'UNKNOWN', message: 'An error occurred' };
    throw new ApiError(error.code, error.message, error.details);
  }

  return data;
}

// Auth
export async function getSession(): Promise<Session> {
  return fetchApi<Session>('/api/auth/session');
}

export async function getDemoAvailable(): Promise<{ available: boolean; googleOAuthAvailable?: boolean; realDataSetupAllowed?: boolean }> {
  return fetchApi<{ available: boolean; googleOAuthAvailable?: boolean; realDataSetupAllowed?: boolean }>('/api/auth/demo-available');
}

export function getGoogleLoginUrl(): string {
  return `${API_BASE_URL}/api/auth/google`;
}


export async function demoLogin(): Promise<{
  success: boolean;
  tenant: { id: string; name: string };
}> {
  return fetchApi('/api/auth/demo-login', { method: 'POST' });
}

export async function logout(): Promise<void> {
  await fetchApi('/api/auth/logout', { method: 'POST' });
}

// Config
export async function getPollingConfig(): Promise<PollingConfig> {
  return fetchApi<PollingConfig>('/api/config/polling');
}

export async function updatePollingConfig(
  config: Partial<Pick<PollingConfig, 'intervalSeconds' | 'isActive' | 'resourceCautionThreshold' | 'resourceCriticalThreshold'>> & {
    pollingFromDate?: string | null;
    pollingToDate?: string | null;
    targetVersion?: string | null;
  }
): Promise<PollingConfig> {
  return fetchApi<PollingConfig>('/api/config/polling', {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

export async function triggerPoll(): Promise<{ success: boolean; message: string }> {
  return fetchApi('/api/config/polling/trigger', { method: 'POST' }, 120_000);
}

export async function triggerDemoPerfect(): Promise<{ success: boolean; message: string }> {
  return fetchApi('/api/config/polling/demo-perfect', { method: 'POST' });
}

// Nodes
export async function getNodes(params?: {
  portfolio?: string;
  state?: string;
  profile?: string;
  search?: string;
  softwareVersion?: string;
  vendor?: string;
  lifecycle?: string;
  tier?: 'Basic' | 'Pro' | 'Enterprise';
  commissionedWithinDays?: number;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<NodesResponse> {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
  }
  const query = searchParams.toString();
  return fetchApi<NodesResponse>(`/api/nodes${query ? `?${query}` : ''}`);
}

export async function getNode(id: string): Promise<{ node: NodeDetail }> {
  return fetchApi<{ node: NodeDetail }>(`/api/nodes/${id}`);
}

export async function getNodeHistory(
  id: string,
  period: Period = '24h'
): Promise<{ history: NodeHistory[]; events: StateEvent[] }> {
  return fetchApi<{ history: NodeHistory[]; events: StateEvent[] }>(
    `/api/nodes/${id}/history?period=${period}`
  );
}

// Metrics
function buildFilterQuery(
  profile?: string,
  vendor?: string,
  softwareVersion?: string,
  portfolio?: string
): string {
  const params = new URLSearchParams();
  if (profile) params.set('profile', profile);
  if (vendor) params.set('vendor', vendor);
  if (softwareVersion) params.set('softwareVersion', softwareVersion);
  if (portfolio) params.set('portfolio', portfolio);
  return params.toString();
}

export async function getFleetSummary(
  profile?: string,
  vendor?: string,
  softwareVersion?: string,
  portfolio?: string
): Promise<FleetSummary> {
  const query = buildFilterQuery(profile, vendor, softwareVersion, portfolio);
  return fetchApi<FleetSummary>(`/api/metrics/summary${query ? `?${query}` : ''}`);
}

export async function getUptimeTrend(
  period: Period = '24h',
  customRange?: { from: Date; to: Date },
  profile?: string,
  vendor?: string,
  softwareVersion?: string,
  portfolio?: string
): Promise<UptimeTrend> {
  const params = new URLSearchParams({ period });
  if (period === 'custom' && customRange) {
    params.set('from', customRange.from.toISOString());
    params.set('to', customRange.to.toISOString());
  }
  if (profile) params.set('profile', profile);
  if (vendor) params.set('vendor', vendor);
  if (softwareVersion) params.set('softwareVersion', softwareVersion);
  if (portfolio) params.set('portfolio', portfolio);
  return fetchApi<UptimeTrend>(`/api/metrics/uptime?${params}`);
}

export async function getPortfolioMetrics(
  profile?: string,
  vendor?: string,
  softwareVersion?: string
): Promise<{ portfolios: PortfolioMetrics[] }> {
  const query = buildFilterQuery(profile, vendor, softwareVersion);
  return fetchApi<{ portfolios: PortfolioMetrics[] }>(
    `/api/metrics/portfolios${query ? `?${query}` : ''}`
  );
}

export async function getPortfolioDetail(
  portfolio: string,
  period: Period = '24h'
): Promise<
  PortfolioMetrics & {
    nodes: NodeSummary[];
    uptimeTrend: { time: string; uptimePercent: number | null }[];
  }
> {
  return fetchApi(`/api/metrics/portfolios/${encodeURIComponent(portfolio)}?period=${period}`);
}

export async function getAlerts(
  profile?: string,
  vendor?: string,
  softwareVersion?: string,
  portfolio?: string
): Promise<{ alerts: Alert[] }> {
  const query = buildFilterQuery(profile, vendor, softwareVersion, portfolio);
  return fetchApi<{ alerts: Alert[] }>(`/api/alerts${query ? `?${query}` : ''}`);
}

export async function getRecentStateChanges(
  limit = 50,
  profile?: string,
  vendor?: string,
  softwareVersion?: string,
  portfolio?: string,
  period?: string,
  customRange?: { from: Date; to: Date }
): Promise<{ changes: StateEvent[] }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (profile) params.set('profile', profile);
  if (vendor) params.set('vendor', vendor);
  if (softwareVersion) params.set('softwareVersion', softwareVersion);
  if (portfolio) params.set('portfolio', portfolio);
  if (period) params.set('period', period);
  if (period === 'custom' && customRange) {
    params.set('startDate', customRange.from.toISOString());
    params.set('endDate', customRange.to.toISOString());
  }
  return fetchApi<{ changes: StateEvent[] }>(`/api/metrics/state-changes?${params}`);
}

// Deployment stats (PKI inventory insights)
export async function getDeploymentStats(): Promise<DeploymentStats> {
  return fetchApi<DeploymentStats>('/api/metrics/deployment-stats');
}

// Node lifecycle counts
export async function getNodeLifecycleCounts(days = 7): Promise<NodeLifecycleCounts> {
  return fetchApi<NodeLifecycleCounts>(`/api/metrics/lifecycle?days=${days}`);
}

// Reports
export async function downloadSlaReport(
  period: Period,
  portfolio?: string,
  format: 'json' | 'csv' = 'csv'
): Promise<Blob | object> {
  const params = new URLSearchParams({ period, format });
  if (portfolio) params.set('portfolio', portfolio);

  const response = await fetch(`${API_BASE_URL}/api/reports/sla?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to download report');
  }

  if (format === 'csv') {
    return response.blob();
  }
  return response.json();
}

export async function downloadInventoryReport(
  format: 'json' | 'csv' = 'csv'
): Promise<Blob | object> {
  const response = await fetch(`${API_BASE_URL}/api/reports/inventory?format=${format}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to download report');
  }

  if (format === 'csv') {
    return response.blob();
  }
  return response.json();
}

// Portfolio bulletin
export async function getPortfolioBulletin(
  portfolio: string,
  period: Period = '30d'
): Promise<{
  portfolio: string;
  period: string;
  generatedAt: string;
  summary: {
    uptimePercent: number;
    totalNodes: number;
    aliveNodes: number;
    unreachableNodes: number;
    incidents: number;
    mttr: number;
  };
  nodes: {
    id: string;
    name: string;
    profileName: string | null;
    currentNodeState: string;
    softwareVersion: string | null;
    lastContactedAt: string | null;
  }[];
  incidents: {
    nodeId: string;
    nodeName: string;
    previousState: string | null;
    newState: string;
    changedAt: string;
    durationSeconds: number | null;
  }[];
  services: {
    name: string;
    nodeId: string;
    nodeName: string;
    healthStatus: string | null;
    runningContainers: string | null;
    totalRestartCount: number;
  }[];
  cloudConnectors: { name: string; currentNodeState: string; portfolio: string | null }[];
}> {
  return fetchApi(
    `/api/reports/portfolio-bulletin?portfolio=${encodeURIComponent(portfolio)}&period=${period}`
  );
}

// Services
export async function getServiceSummary(
  profile?: string,
  vendor?: string,
  softwareVersion?: string,
  portfolio?: string
): Promise<ServiceSummary> {
  const query = buildFilterQuery(profile, vendor, softwareVersion, portfolio);
  return fetchApi<ServiceSummary>(`/api/metrics/services${query ? `?${query}` : ''}`);
}

// User audit
export async function getUserAudit(): Promise<UserAudit> {
  return fetchApi<UserAudit>('/api/metrics/user-audit');
}

// Cluster summary
export async function getClusterSummary(): Promise<ClusterSummary> {
  return fetchApi<ClusterSummary>('/api/metrics/clusters');
}

// Network summary
export async function getNetworkSummary(): Promise<NetworkSummary> {
  return fetchApi<NetworkSummary>('/api/metrics/networks');
}

// Network list (paginated)
export async function getNetworksList(params?: {
  type?: string;
  segment?: string;
  nodeId?: string;
  search?: string;
  tunnelState?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<NetworksResponse> {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
  }
  const query = searchParams.toString();
  return fetchApi<NetworksResponse>(`/api/networks${query ? `?${query}` : ''}`);
}

// Resources
export async function getResourceSummary(
  profile?: string,
  vendor?: string,
  softwareVersion?: string,
  portfolio?: string
): Promise<ResourceSummary> {
  const query = buildFilterQuery(profile, vendor, softwareVersion, portfolio);
  return fetchApi<ResourceSummary>(`/api/metrics/resources${query ? `?${query}` : ''}`);
}

// Node context (for problem drill-down)
export async function getNodeContext(id: string): Promise<NodeContext> {
  return fetchApi<NodeContext>(`/api/nodes/${id}/context`);
}

// Users page
export async function getUserPageSummary(): Promise<UserPageSummary> {
  return fetchApi<UserPageSummary>('/api/users/summary');
}

export async function getUsersList(params?: {
  role?: string;
  flag?: string;
  enabled?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<UsersResponse> {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
  }
  const query = searchParams.toString();
  return fetchApi<UsersResponse>(`/api/users${query ? `?${query}` : ''}`);
}

export async function getUserDetail(userId: string): Promise<UserDetail> {
  return fetchApi<UserDetail>(`/api/users/${encodeURIComponent(userId)}`);
}

export async function getUserRoles(): Promise<RolePermissionsItem[]> {
  return fetchApi<RolePermissionsItem[]>('/api/users/roles');
}


// Triage
export async function getTriageQueue(params?: {
  priority?: TriagePriority;
  category?: string;
  portfolio?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<TriageQueueResponse> {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
  }
  const query = searchParams.toString();
  return fetchApi<TriageQueueResponse>(`/api/triage/problems${query ? `?${query}` : ''}`);
}

export async function getFleetRiskForecast(): Promise<FleetRiskForecast> {
  return fetchApi<FleetRiskForecast>('/api/triage/risk-forecast');
}

// Security / Firewall Intelligence
export async function getSecuritySummary(): Promise<SecuritySummary> {
  return fetchApi<SecuritySummary>('/api/security/summary');
}

export async function getFirewallGroups(params?: {
  ids?: string[];
  search?: string;
  hasOverlyPermissive?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<{ groups: FirewallGroupItem[]; total: number; totalPages: number }> {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'ids' && Array.isArray(value)) {
          if (value.length > 0) searchParams.set('ids', value.join(','));
        } else {
          searchParams.set(key, String(value));
        }
      }
    });
  }
  const query = searchParams.toString();
  return fetchApi<{ groups: FirewallGroupItem[]; total: number; totalPages: number }>(
    `/api/security/firewall-groups${query ? `?${query}` : ''}`
  );
}

export async function getFirewallHitsByNode(): Promise<FirewallHitsByNode[]> {
  return fetchApi<FirewallHitsByNode[]>('/api/security/firewall-hits-by-node');
}

export async function getRulesPerNetwork(segment?: string): Promise<RulesPerNetwork[]> {
  const params = new URLSearchParams();
  if (segment) params.set('segment', segment);
  const query = params.toString();
  return fetchApi<RulesPerNetwork[]>(`/api/security/rules-per-network${query ? `?${query}` : ''}`);
}

export async function getDeniedTrafficTrend(period = '30d', networkName?: string, nodeName?: string): Promise<DeniedTrafficPoint[]> {
  const params = new URLSearchParams({ period });
  if (networkName) params.set('networkName', networkName);
  if (nodeName) params.set('nodeName', nodeName);
  return fetchApi<DeniedTrafficPoint[]>(`/api/security/denied-trend?${params}`);
}

export async function getDeniedPacketsForPeriod(period = '24h'): Promise<DeniedPacketsPeriod> {
  return fetchApi<DeniedPacketsPeriod>(`/api/security/denied-packets?period=${period}`);
}

// Chat
export async function sendChatMessage(
  message: string,
  conversationHistory: { role: 'user' | 'assistant'; content: string }[] = []
): Promise<{ message: string }> {
  return fetchApi<{ message: string }>('/api/chat', {
    method: 'POST',
    body: JSON.stringify({ message, conversationHistory }),
  });
}

// ── Auth: New multi-tenant endpoints ────────────────────────────────

export async function lookupEmail(email: string): Promise<AuthLookupResult> {
  return fetchApi<AuthLookupResult>('/api/auth/lookup', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function seLegacyLogin(
  email: string,
  password: string,
  otp?: string
): Promise<{ success: boolean; mfaRequired?: boolean; tenant?: { id: string; name: string } }> {
  return fetchApi('/api/auth/se-login', {
    method: 'POST',
    body: JSON.stringify({ email, password, otp }),
  }, 30_000);
}

// ── Admin: Organization management ─────────────────────────────────

export async function getAdminOrganizations(platformId?: string): Promise<{ organizations: Organization[] }> {
  const params = platformId ? `?platform=${platformId}` : '';
  return fetchApi<{ organizations: Organization[] }>(`/api/admin/organizations${params}`);
}

export async function getPendingOrganizations(platformId?: string): Promise<{ organizations: Organization[] }> {
  const params = platformId ? `?platform=${platformId}` : '';
  return fetchApi<{ organizations: Organization[] }>(`/api/admin/organizations/pending${params}`);
}

export async function approveOrganization(id: string, classification?: string): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/organizations/' + id + '/approve', {
    method: 'POST',
    body: JSON.stringify({ classification }),
  });
}

export async function denyOrganization(id: string): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/organizations/' + id + '/deny', { method: 'POST' });
}

export async function decommissionOrganization(id: string): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/organizations/' + id + '/decommission', { method: 'POST' });
}

export async function updateOrgClassification(id: string, classification: string): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/organizations/' + id + '/classification', {
    method: 'PUT',
    body: JSON.stringify({ classification }),
  });
}

export async function updateOrgPolling(id: string, enabled: boolean): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/organizations/' + id + '/polling', {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

export async function getOrgDiagnostics(id: string): Promise<{
  org: { id: string; name: string; status: string; pollingEnabled: boolean; tenantId: string | null; lastSuccessfulPoll: string | null; lastPollError: string | null };
  tenant: { id: string; name: string; dataCounts: { nodes: number; neeveUsers: number; networks: number } } | null;
  rootKeyConfigured: boolean;
}> {
  return fetchApi('/api/admin/organizations/' + id + '/diagnostics');
}

export async function pollOrganization(id: string): Promise<{
  success: boolean;
  tenantId: string;
  dataCounts: { nodes: number; neeveUsers: number; networks: number };
}> {
  return fetchApi('/api/admin/organizations/' + id + '/poll', { method: 'POST' }, 60_000);
}

export async function updateOrgRetention(id: string, days: number): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/organizations/' + id + '/retention', {
    method: 'PUT',
    body: JSON.stringify({ days }),
  });
}

export async function updateOrgStatus(id: string, status: 'active' | 'disabled'): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/organizations/' + id + '/status', {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export async function exportOrgData(id: string): Promise<void> {
  const url = `${API_BASE_URL}/api/admin/organizations/${id}/export`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `org-export-${id}-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function purgeOrgData(id: string): Promise<{ success: boolean; deletedCounts: Record<string, number> }> {
  return fetchApi('/api/admin/organizations/' + id + '/purge', { method: 'DELETE' });
}

// ── Polling Controls ────────────────────────────────────────────────

export interface PollingStatus {
  paused: boolean;
  pausedAt: string | null;
  pausedBy: string | null;
  pauseReason: string | null;
  pollCycleCounter: number;
  pollingConfig: Record<string, unknown>;
  endpointTierConfig: Record<string, unknown>;
  openCircuitBreakers: Array<{
    id: string;
    name: string;
    consecutivePollFailures: number;
    circuitBreakerOpenUntil: string;
  }>;
  masterPaused?: boolean;
  platformConfigId?: string;
}

export async function getPollingStatus(platformId?: string): Promise<PollingStatus> {
  const params = platformId ? `?platform=${platformId}` : '';
  return fetchApi(`/api/admin/polling/status${params}`);
}

export async function pausePolling(reason?: string, platformConfigId?: string): Promise<{ success: boolean }> {
  // Use per-platform endpoint when platformConfigId is provided
  if (platformConfigId) {
    return fetchApi(`/api/admin/platform-configs/${platformConfigId}/pause`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }
  return fetchApi('/api/admin/polling/pause', {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  });
}

export async function resumePolling(platformConfigId?: string): Promise<{ success: boolean }> {
  if (platformConfigId) {
    return fetchApi(`/api/admin/platform-configs/${platformConfigId}/resume`, { method: 'PUT' });
  }
  return fetchApi('/api/admin/polling/resume', { method: 'PUT' });
}

export async function updatePollingEngineConfig(config: Record<string, unknown>, platformConfigId?: string): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/polling/config', {
    method: 'PUT',
    body: JSON.stringify({ ...config, platformConfigId }),
  });
}

export async function updateTierConfig(tiers: Record<string, unknown>): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/polling/tiers', {
    method: 'PUT',
    body: JSON.stringify(tiers),
  });
}

export interface PollCycleLogEntry {
  id: string;
  cycleNumber: number;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  tiersRun: string[];
  orgsAttempted: number;
  orgsSucceeded: number;
  orgsFailed: number;
  orgsSkipped: number;
  orgsDeferred: number;
  apiCallsMade: number;
  errorSummary: Record<string, number> | null;
  trigger: string;
}

export interface PollCycleOrgResult {
  id: number;
  cycleId: string;
  orgId: string;
  orgName: string;
  status: 'succeeded' | 'failed' | 'deferred' | 'circuit_broken';
  tiersRun: string[];
  durationMs: number | null;
  apiCalls: number;
  error: string | null;
  createdAt: string;
}

export async function getPollingCycles(
  limit = 100,
  platformId?: string,
  offset = 0,
  from?: string,
  to?: string
): Promise<{ cycles: PollCycleLogEntry[]; total: number }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (platformId) params.set('platform', platformId);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return fetchApi(`/api/admin/polling/cycles?${params}`);
}

export async function getPollingCycleOrgs(cycleId: string): Promise<{ results: PollCycleOrgResult[] }> {
  return fetchApi(`/api/admin/polling/cycles/${cycleId}/orgs`);
}

export async function syncHierarchy(platformConfigId?: string): Promise<{ success: boolean; summary: { total: number; new: number; updated: number; dbCount?: number; rawApiCount?: number; pagesFetched?: number } }> {
  return fetchApi('/api/admin/sync-hierarchy', {
    method: 'POST',
    body: JSON.stringify({ platformConfigId }),
  }, 60_000);
}

export async function getRootConfig(): Promise<{
  isConfigured: boolean;
  portalUrl: string | null;
  pollingIntervalSeconds: number | null;
  lastHierarchySyncAt: string | null;
  isActive: boolean;
}> {
  return fetchApi('/api/admin/root-config');
}

export async function getAuth0Config(): Promise<{ configured: boolean; domain: string | null; clientId: string | null }> {
  return fetchApi('/api/admin/root-config/auth0');
}

export async function updateAuth0Config(
  domain: string,
  clientId: string,
  clientSecret: string
): Promise<{ success: boolean }> {
  return fetchApi('/api/admin/root-config/auth0', {
    method: 'PUT',
    body: JSON.stringify({ domain, clientId, clientSecret }),
  });
}

export interface Auth0ConfigEntry {
  id: string;
  label: string;
  domain: string;
  clientId: string;
  isDefault: boolean;
  orgCount: number;
}

export async function getAuth0Configs(): Promise<{ configs: Auth0ConfigEntry[] }> {
  return fetchApi('/api/admin/auth0-configs');
}

export async function upsertAuth0Config(
  label: string,
  domain: string,
  clientId: string,
  clientSecret: string,
  isDefault: boolean
): Promise<{ success: boolean; id: string }> {
  return fetchApi('/api/admin/auth0-configs', {
    method: 'PUT',
    body: JSON.stringify({ label, domain, clientId, clientSecret, isDefault }),
  });
}

export async function deleteAuth0Config(id: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/admin/auth0-configs/${id}`, { method: 'DELETE' });
}

// ── Platform Configs ─────────────────────────────────────────────────

export async function getPlatformConfigs(): Promise<{ configs: PlatformConfig[] }> {
  return fetchApi('/api/admin/platform-configs');
}

export async function upsertPlatformConfig(data: {
  label: string;
  slug: string;
  rootPortalUrl: string;
  portalUrlSuffix: string;
  apiKey?: string;
  isProduction?: boolean;
  auth0ConfigId?: string | null;
}): Promise<{ success: boolean; id: string }> {
  return fetchApi('/api/admin/platform-configs', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePlatformConfig(id: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/admin/platform-configs/${id}`, { method: 'DELETE' });
}

export async function pausePlatformPolling(id: string, reason?: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/admin/platform-configs/${id}/pause`, {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  });
}

export async function resumePlatformPolling(id: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/admin/platform-configs/${id}/resume`, { method: 'PUT' });
}

export async function purgeData(targetTenantId?: string, confirm?: boolean): Promise<{ success: boolean; purged: Record<string, number> }> {
  return fetchApi<{ success: boolean; purged: Record<string, number> }>('/api/config/data/purge', {
    method: 'POST',
    body: JSON.stringify({ targetTenantId, confirm }),
  });
}

// ── Dev Audit Trail ──────────────────────────────────────────────────

export interface DevAuditEntry {
  id: number;
  email: string;
  name: string;
  action: string;
  targetOrgId: string | null;
  targetOrgName: string | null;
  details: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export async function getAuditLog(limit = 100, offset = 0): Promise<{ logs: DevAuditEntry[]; total: number }> {
  return fetchApi(`/api/admin/audit-log?limit=${limit}&offset=${offset}`);
}

export function getAuditExportUrl(): string {
  return `${API_BASE_URL}/api/admin/audit-log/export`;
}

// ── Data Export ──────────────────────────────────────────────────────

export function getExportUrl(): string {
  return `${API_BASE_URL}/api/export/full?format=json`;
}

// ── Insights API Keys ───────────────────────────────────────────────

export interface InsightsApiKeyInfo {
  id: string;
  keyPrefix: string;
  label: string;
  createdByEmail: string;
  orgScope: string[];
  permissions: string[];
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface CreateApiKeyResult {
  key: InsightsApiKeyInfo & { plaintext: string };
}

export async function createApiKey(label: string, expiresAt?: string): Promise<CreateApiKeyResult> {
  return fetchApi('/api/v1/developer/keys', {
    method: 'POST',
    body: JSON.stringify({ label, expiresAt }),
  });
}

export async function listApiKeys(): Promise<{ keys: InsightsApiKeyInfo[] }> {
  return fetchApi('/api/v1/developer/keys');
}

export async function revokeApiKey(id: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/v1/developer/keys/${id}`, { method: 'DELETE' });
}

export async function updateApiKey(id: string, data: { label?: string; expiresAt?: string | null }): Promise<{ key: InsightsApiKeyInfo }> {
  return fetchApi(`/api/v1/developer/keys/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ── Webhooks ────────────────────────────────────────────────────────

export interface WebhookEndpointInfo {
  id: string;
  apiKeyId: string;
  url: string;
  eventTypes: string[];
  isActive: boolean;
  lastDeliveryAt: string | null;
  failureCount: number;
  createdAt: string;
}

export async function createWebhook(data: { apiKeyId: string; url: string; eventTypes: string[] }): Promise<{ endpoint: WebhookEndpointInfo; secret: string }> {
  return fetchApi('/api/v1/developer/webhooks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function listWebhooks(): Promise<{ endpoints: WebhookEndpointInfo[] }> {
  return fetchApi('/api/v1/developer/webhooks');
}

export async function deleteWebhook(id: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/v1/developer/webhooks/${id}`, { method: 'DELETE' });
}

export async function testWebhook(id: string): Promise<{ success: boolean; statusCode?: number; error?: string }> {
  return fetchApi(`/api/v1/developer/webhooks/${id}/test`, { method: 'POST' });
}

// ── Org-scoped integrations (Settings → API & Webhooks) ─────────────
// Same shapes as the developer endpoints, but scoped server-side to the
// session's own organization. Keys created here can only read that org's data.

export async function createOrgApiKey(label: string, expiresAt?: string): Promise<CreateApiKeyResult> {
  return fetchApi('/api/integrations/keys', {
    method: 'POST',
    body: JSON.stringify({ label, expiresAt }),
  });
}

export async function listOrgApiKeys(): Promise<{ keys: InsightsApiKeyInfo[] }> {
  return fetchApi('/api/integrations/keys');
}

export async function revokeOrgApiKey(id: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/integrations/keys/${id}`, { method: 'DELETE' });
}

export async function createOrgWebhook(data: { apiKeyId: string; url: string; eventTypes: string[] }): Promise<{ endpoint: WebhookEndpointInfo; secret: string }> {
  return fetchApi('/api/integrations/webhooks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function listOrgWebhooks(): Promise<{ endpoints: WebhookEndpointInfo[] }> {
  return fetchApi('/api/integrations/webhooks');
}

export async function deleteOrgWebhook(id: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/integrations/webhooks/${id}`, { method: 'DELETE' });
}

export async function testOrgWebhook(id: string): Promise<{ success: boolean; statusCode?: number; error?: string }> {
  return fetchApi(`/api/integrations/webhooks/${id}/test`, { method: 'POST' });
}

// ── Analytics dashboards (RA datalake backed) ─────────────────────────────

/**
 * Build a filter query string from `useDashboardFilters` state. Keeps the
 * caller call-sites concise: `getActiveUsers(buildAnalyticsQuery(filters))`.
 */
function buildAnalyticsQuery(filters: {
  dateRange?:
    | { kind: 'all-time' }
    | { kind: 'relative'; unit: string; count: number; excludeToday: boolean }
    | { kind: 'static'; from: string; to: string };
  orgs?: string[];
  nodes?: string[];
}): string {
  const params = new URLSearchParams();
  const r = filters.dateRange;
  if (r && r.kind === 'relative') {
    params.set('dateKind', 'relative');
    params.set('dateUnit', r.unit);
    params.set('dateCount', String(r.count));
    if (r.excludeToday) params.set('excludeToday', '1');
  } else if (r && r.kind === 'static') {
    params.set('dateKind', 'static');
    params.set('dateFrom', r.from);
    params.set('dateTo', r.to);
  }
  if (filters.orgs && filters.orgs.length > 0) params.set('orgs', filters.orgs.join(','));
  if (filters.nodes && filters.nodes.length > 0) params.set('nodes', filters.nodes.join(','));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getActiveUsersAnalytics(qs: string): Promise<import('@/types/analytics').ActiveUsersDataset> {
  return fetchApi(`/api/analytics/active-users${qs}`);
}

export async function getActiveEndpointsAnalytics(qs: string): Promise<import('@/types/analytics').ActiveEndpointsDataset> {
  return fetchApi(`/api/analytics/active-endpoints${qs}`);
}

export async function getOverviewRaAnalytics(qs: string): Promise<import('@/types/analytics').OverviewRaDataset> {
  return fetchApi(`/api/analytics/overview${qs}`);
}

export { buildAnalyticsQuery };

// ── Feature Override Admin (Dev Console) ──────────────────────────────────

export interface FeatureDefinitionDto {
  id: string;
  stage: 'mvp' | 'base' | 'deep';
  scope: 'route' | 'widget' | 'capability' | 'dev-console';
  label: string;
  description?: string;
}

export interface FeatureOverrideRow {
  id: string;
  organizationId: string;
  organizationName: string | null;
  featureId: string;
  enabled: boolean;
  reason: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeatureOverrideAuditRow {
  id: number;
  organizationId: string;
  organizationName: string | null;
  featureId: string;
  action: string;
  previousValue: string | null;
  newValue: string;
  reason: string | null;
  actorEmail: string;
  actorIp: string | null;
  createdAt: string;
}

export async function listFeatureCatalog(): Promise<{ features: FeatureDefinitionDto[] }> {
  return fetchApi('/api/v1/developer/features');
}

export async function listFeatureOverrides(filter?: {
  organizationId?: string;
  featureId?: string;
}): Promise<{ overrides: FeatureOverrideRow[] }> {
  const params = new URLSearchParams();
  if (filter?.organizationId) params.set('organizationId', filter.organizationId);
  if (filter?.featureId) params.set('featureId', filter.featureId);
  const qs = params.toString();
  return fetchApi(`/api/v1/developer/feature-overrides${qs ? `?${qs}` : ''}`);
}

export async function listFeatureOverrideAudit(filter?: {
  organizationId?: string;
  featureId?: string;
  limit?: number;
}): Promise<{ audit: FeatureOverrideAuditRow[] }> {
  const params = new URLSearchParams();
  if (filter?.organizationId) params.set('organizationId', filter.organizationId);
  if (filter?.featureId) params.set('featureId', filter.featureId);
  if (filter?.limit) params.set('limit', String(filter.limit));
  const qs = params.toString();
  return fetchApi(`/api/v1/developer/feature-overrides/audit${qs ? `?${qs}` : ''}`);
}

export async function upsertFeatureOverride(input: {
  organizationId: string;
  featureId: string;
  enabled: boolean;
  reason?: string;
}): Promise<{ override: FeatureOverrideRow }> {
  return fetchApi('/api/v1/developer/feature-overrides', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function removeFeatureOverride(
  organizationId: string,
  featureId: string
): Promise<{ removed: true }> {
  return fetchApi(`/api/v1/developer/feature-overrides/${organizationId}/${featureId}`, {
    method: 'DELETE',
  });
}

// ── Additional SE metrics (Overview Apps & Services + Product Tier) ──────

export async function getServicesByName(): Promise<{
  servicesByName: { name: string; count: number }[];
}> {
  return fetchApi('/api/metrics/services-by-name');
}

export async function getProductTierBreakdown(): Promise<{
  Basic: number;
  Pro: number;
  Enterprise: number;
}> {
  return fetchApi('/api/metrics/product-tier');
}
